<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';

	import { Button, ButtonV2, Modal } from '@Shelly/shelly-design-system';

	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import type { CameraCloudStorageActivePlan } from 'src/types/CameraCloudStorage';

	import ManageCameraCloudStoragePanel from './ManageCameraCloudStoragePanel.svelte';

	/**
	 * Manage Camera Cloud Storage modal — entry point for assigning,
	 * replacing or removing cameras occupying Cloud Storage slots.
	 *
	 * On Save the modal POSTs the flattened device-id list to Spas's
	 * `POST /user/set_camera_list` endpoint.
	 */
	export let showModal = false;
	export let activePlan: CameraCloudStorageActivePlan | null = null;
	/** Open the modal directly on the Add-camera step (used by the post-subscribe wizard). */
	export let openOnAddStep = false;
	/**
	 * When provided, the modal opens with this camera already placed in
	 * the first empty slot (draft-only — nothing is saved until the user
	 * clicks "Save").
	 */
	export let preselectedCameraId: string | undefined;
	/** Dev Utilities flow preview */
	export let slotCountOverride: number | null = null;
	export let previewMode = false;

	const dispatch = createEventDispatcher<{
		close: void;
		upgrade: void;
	}>();

	$: ButtonComp = $showNewLayout ? ButtonV2 : Button;

	let panel;
	let view: 'main' | 'add' | 'replace' = 'main';
	let saving = false;
	let hasDirtyChanges = false;
	let pickerSelection: string[] = [];

	function handleClose() {
		showModal = false;
		dispatch('close');
	}

	function handleModalClose() {
		if (openOnAddStep || view === 'main') {
			handleClose();
			return;
		}
		panel?.cancelPicker();
	}

	async function handleSave() {
		const ok = await panel?.saveAssignments();
		if (ok) handleClose();
	}

	$: modalTitle =
		view === 'add'
			? $format('modal_title_fill_empty_camera_slots', {
					default: 'Fill the empty camera slots',
				})
			: view === 'replace'
				? $format('modal_title_replace_camera_in_slot', {
						default: 'Replace camera',
					})
				: $format('modal_title_manage_camera_cloud_storage', {
						default: 'Manage Camera Cloud Storage',
					});
</script>

{#if showModal}
	<Modal {showModal} on:close={handleModalClose} title={modalTitle} removeScrollPadding>
		<div slot="content">
			<ManageCameraCloudStoragePanel
				bind:this={panel}
				bind:view
				bind:saving
				bind:hasDirtyChanges
				bind:pickerSelection
				{activePlan}
				{openOnAddStep}
				{preselectedCameraId}
				{slotCountOverride}
				{previewMode}
				on:upgrade
				on:close={handleClose}
			/>
		</div>

		<div slot="footer" class="d-flex justify-content-end gap-s">
			{#if view === 'main'}
				<svelte:component
					this={ButtonComp}
					variant="secondary"
					size="m"
					disabled={saving}
					on:click={handleClose}
				>
					{$format('button_cancel', { default: 'Cancel' })}
				</svelte:component>
				<svelte:component
					this={ButtonComp}
					variant="primary"
					size="m"
					loading={saving}
					disabled={!hasDirtyChanges || saving}
					on:click={handleSave}
				>
					{preselectedCameraId
						? $format('button_assign_camera', { default: 'Assign camera' })
						: $format('button_save', { default: 'Save' })}
				</svelte:component>
			{:else}
				<svelte:component
					this={ButtonComp}
					variant="secondary"
					size="m"
					on:click={() => panel?.cancelPicker()}
				>
					{$format('button_cancel', { default: 'Cancel' })}
				</svelte:component>
				<svelte:component
					this={ButtonComp}
					variant="primary"
					size="m"
					disabled={pickerSelection.length === 0}
					on:click={() => (view === 'add' ? panel?.applyAdd() : panel?.applyReplace())}
				>
					{$format('button_select_camera', { default: 'Select camera' })}
				</svelte:component>
			{/if}
		</div>
	</Modal>
{/if}
